import { lazy, Suspense, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';

import 'rsuite/dist/rsuite-no-reset.min.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './assets/css/style.css';
import './assets/css/Loader.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import Loader from './jsx/pages/bootstrap/Loader';
import Error404 from './jsx/pages/error/Error404';
import { Modal } from 'react-bootstrap';

import { isAuthenticated } from './store/selectors/AuthSelectors';
import { checkAutoLogin, formatError } from './services/AuthService';
import { fetchLoggedInUserActNav, consentResponseAction } from './store/actions/AuthActions';


import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Filler,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { sendOTP } from './store/actions/PreferencesActions';
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Filler,
  ChartDataLabels
);


const SignUp = lazy(() => import('./jsx/pages/authentication/Registration'));
const Login = lazy(() =>
  new Promise(resolve => setTimeout(() => resolve(import('./jsx/pages/authentication/Login')), 500))
);
const Index = lazy(() => import('./jsx/router/index'));

function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    let location = useLocation();
    let navigate = useNavigate();
    let params = useParams();
    return <Component {...props} router={{ location, navigate, params }} />;
  }
  return ComponentWithRouterProp;
}

function App(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [checkUserInterval, setCheckUserInterval] = useState(null);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showOtpField, setShowOtpField] = useState(false);
  const [consentResponse, setConsentResponse] = useState("");
  const [otp, setOtp] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [showResendButton, setShowResendButton] = useState(false);
  const chars = ['H', 'R', 'D'];

  useEffect(() => {
    checkAutoLogin(dispatch, navigate);
  }, []);

  useEffect(() => {
    if (props.isAuthenticated) {
      const interval = setInterval(() => {
        dispatch(fetchLoggedInUserActNav(navigate));
      }, 10000);
      setCheckUserInterval(interval);
    } else {
      clearInterval(checkUserInterval);
    }
  }, [props.isAuthenticated]);

  useEffect(() => {
    const consentSubmitted = sessionStorage.getItem('consentSubmitted');
    
    if (props.consentMessage && !showConsentModal && consentSubmitted !== 'true') {
      setShowConsentModal(true);
    }
  }, [props.consentMessage, showConsentModal]);

  useEffect(() => {
    if (props.consentActionResponse) {
      setShowConsentModal(false);
      setShowOtpField(false);
      setOtp('');
      // Store consent submission flag in sessionStorage
      sessionStorage.setItem('consentSubmitted', 'true');
    }
   }, [props.consentActionResponse, props.consentMessage, showConsentModal]);

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prevTimer => prevTimer - 1);
      }, 1000);
    } else if (resendTimer === 0 && showResendButton) {
      setShowResendButton(false);
    }
    return () => clearInterval(interval);
  }, [resendTimer, showResendButton]);

  const AuthRoutes = (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* Add other public routes if needed */}
      {/* <Route path="*" element={<Error404 />} /> */}
    </Routes>
  );

  const mesg = "मैं अपने प्राथमिकता प्रपत्र की पुष्टि करने के लिए सहमत हूं। मुझे पता है कि एक बार वरीयता प्रपत्र की पुष्टि करने के बाद मैं अपने विकल्प में संशोधन/परिवर्तन नहीं कर पाऊंगा/पाऊंगी।";
  const handleConsentResponse = (response) => {
    dispatch(sendOTP(mesg, "admin"));
    setConsentResponse(response);
    // setShowConsentModal(false);
    setShowOtpField(true);
    setResendTimer(120); // 2 minutes in seconds
    setShowResendButton(true);
  };

  const handleOtpVerification = () => {
    if (!otp || otp.trim() === '') {
      formatError("OTP cannot be empty");
      return;
    }
    
    if (otp.length !== 6) {
      formatError("OTP must be exactly 6 digits");
      return;
    }
    
    const otpData = {
      response: consentResponse,
      otp: otp
    };
    dispatch(consentResponseAction(otpData));
    
  };

  return (
    <>
  
      {props.showLoading && (
        <div className="loader-overlay">
          <Loader />
        </div>
      )}

   
      {props.isAuthenticated ? (
        <Suspense
          fallback={
            <div className="loaderBody">
              <div className="loader">
                {chars.map((char, index) => (
                  <span key={index} style={{ '--i': index + 1 }}>{char}</span>
                ))}
              </div>
            </div>
          }
        >
          <Index />
        </Suspense>
      ) : (
     
        <div className="vh-100">
          <Suspense
            fallback={
              <div className="loaderBody">
                <div className="loader">
                  {chars.map((char, index) => (
                    <span key={index} style={{ '--i': index + 1 }}>{char}</span>
                  ))}
                </div>
              </div>
            }
          >
            {AuthRoutes}
          </Suspense>
        </div>
      )}

      <Modal className="modal fade " show={showConsentModal} backdrop="static" keyboard={false} centered>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Consent Message</h5>
          </div>
          <div className="modal-body">
            <p>{props.consentMessage}</p>
            {showOtpField && (
              <div className="mt-3">
                <label className="mb-1"><strong>OTP</strong></label>
                <input
                  type="text"
                  className="form-control"
                  value={otp}
                  placeholder="Type Your OTP"
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength={6}
                  onKeyDown={(e) => {
                    if (
                      !(
                        (e.key >= '0' && e.key <= '9') || 
                        e.key === 'Backspace' || 
                        e.key === 'ArrowLeft' || 
                        e.key === 'ArrowRight' || 
                        e.key === 'Delete' || 
                        e.key === 'Tab'
                      )
                    ) {
                      e.preventDefault();
                    }
                  }}
                />
              </div>
            )}
          </div>
          <div className="modal-footer">
            {!showOtpField ? (
              <div className="w-100 d-flex justify-content-center gap-5">
                <button 
                  type="button" 
                  className="btn btn-primary me-2" 
                  style={{ width: "30%" }} 
                  onClick={() => { 
                    handleConsentResponse('YES');
                  }}
                >
                  Yes
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  style={{ width: "30%" }} 
                  onClick={() => { 
                    handleConsentResponse('NO');
                  }}
                >
                  No
                </button>
              </div>
            ) : (
              <div className="w-100 d-flex justify-content-center gap-5">
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  style={{ width: "40%" }} 
                  onClick={() => { 
                    handleOtpVerification(); 
                  }}
                >
                  Submit OTP
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  style={{ width: "40%" }} 
                  onClick={() => { 
                    dispatch(sendOTP(mesg, "candidate"));
                    setResendTimer(120); // Reset timer to 2 minutes
                    setShowResendButton(true);
                  }}
                  disabled={resendTimer > 0}
                >
                  {resendTimer > 0 ? `Resend OTP (${Math.floor(resendTimer / 60)}:${String(resendTimer % 60).padStart(2, '0')})` : 'Resend OTP'}
                </button>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}

const mapStateToProps = (state) => ({
  isAuthenticated: isAuthenticated(state),
  showLoading: state.auth.showLoading, 
  consentMessage: state.auth.consentMessage, 
  consentActionResponse: state.auth.consentActionResponse, 
});

export default withRouter(connect(mapStateToProps)(App));
